import React, { useState, useRef } from 'react';
import moment from 'moment';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import clsx from 'clsx';
import styles from './Sidebar.module.scss';
import { Accordion } from './components';
import { ReactComponent as ChevronUpIcon } from 'assets/images/ChevronUp.svg';
import { ReactComponent as InfoIcon } from 'assets/images/Sidebar--info.svg';
import { ReactComponent as ShareCloseIcon } from 'assets/images/Close.svg';
import { ReactComponent as ShareDownIcon } from 'assets/images/Share--Modal--Down.svg';
import Share1Icon from 'assets/images/Share1.png';
import Share2Icon from 'assets/images/Share2.png';
import Share3Icon from 'assets/images/Share3.png';
import { useStateValues } from 'providers';
import EventList from './EventList';
import { useOutsideAlerter } from 'hooks/useOutsideAlerter';

const Sidebar = ({
  className,
  eventTypeIds = [],
  setEventTypeIds = () => null,
  isOpened = false,
  onClose = () => null
}) => {
  const { state: { selectedEvents, selectedDate, showShareModal }, action: { setShowShareModal } } = useStateValues();
  const website_url = window.location.origin;
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, () => {
    setShowShareModal(false);
    setTimeout(() => {
      setShareButton(false);
    }, [200]);
  });
  const [eventTypes, setEventTypes] = useState(
    [{
      id: 'in_game',
      label: 'In-Game Events',
      subTypes: [{
        id: 'recurring',
        label: 'Include Recurring Events',
        helper: <InfoIcon />
      }]
    }, {
      id: 'race_of_legends',
      label: 'Race of Legends'
    }, {
      id: 'community',
      label: 'Community Events'
    }, {
      id: 'twitch_drop',
      label: 'Twitch Drops'
    }, {
      id: 'sot_shot',
      label: '#SoTShot'
    }]
  );
  const [shareButton, setShareButton] = useState(false);

  const renderCheckboxItem = ({ id, label, helper, subTypes, opened = true }) => {
    const checked = eventTypeIds.includes(id);

    const handleToggleSubMenu = e => {
      e.preventDefault();

      setEventTypes(eventTypes => {
        const newTypes = [];

        for (const type of eventTypes) {
          if (type.id === id) type.opened = !opened;
          newTypes.push(type)
        }

        return newTypes;
      });
    };

    return (
      <React.Fragment key={id}>
        <div className={styles.check_box__wrapper}>
          <div
            className={styles.check_box}
            onClick={() => setEventTypeIds(checked ? eventTypeIds.filter(typeId => typeId !== id) : [].concat(eventTypeIds, id))}
          >
            <span className={clsx(styles.check_box__icon, checked && styles['check_box__icon--checked'])} />
            <span className={styles.check_box__label} dangerouslySetInnerHTML={{ __html: label }} />
          </div>

          {subTypes ? (
            <button
              className={clsx(styles.check_box__chevron, styles[`check_box__chevron--${opened ? 'up' : 'down'}`])}
              onClick={handleToggleSubMenu}
            ><ChevronUpIcon /></button>
          ) : null}

          {helper ? <button className={styles.check_box__helper}>{helper}</button> : null}
        </div>

        {subTypes ? (
          <div
            className={clsx(styles.check_box__sub_menu, opened && styles['check_box__sub_menu--opened'])}
            style={{ '--max-height': `${36 * subTypes.length}px` }}
          >
            {subTypes.map(renderCheckboxItem)}
          </div>
        ) : null}
      </React.Fragment>
    );
  };

  return (
    <div className={clsx(styles.sidebar, isOpened && styles['sidebar--opened'])}>

      <button
        className={styles.sidebar__close}
        onClick={onClose}
      />

      <button
        className={styles.sidebar__share}
        dangerouslySetInnerHTML={{ __html: '' }}
        onClick={() => {
          !shareButton && setShowShareModal(true);
          setShareButton(true);
        }}
      />

      {showShareModal && (
        <div className={styles.sidebar__share__modal} ref={wrapperRef}>
          <ShareDownIcon className={styles.sidebar__share__modal__down} />
          <ShareCloseIcon
            className={styles.sidebar__share__modal__close}
            onClick={() => {
              setShowShareModal(false)
              setTimeout(() => {
                setShareButton(false);
              }, [200]);
            }}
          />
          <div className={styles.sidebar__share__modal__socials}>
            <a rel="noreferrer" target='_blank' href={`https://www.reddit.com/submit?url=${website_url}&title=The%20to%20Pirate%20Calendar`}><img src={Share1Icon} alt="share" /></a>
            <CopyToClipboard text={website_url} onCopy={() => console.log("copied")}>
              <img src={Share2Icon} alt="share" />
            </CopyToClipboard>
            <a rel="noreferrer" target='_blank' href={`https://twitter.com/intent/tweet?url=${website_url}&text=Don%27t%20miss%20out%20on%20your%20next%20adventure%20in%20the%20Sea%20of%20Thieves%21%20Be%20sure%20to%20check%20out%20The%20Pirate%20Calendar%21`}><img src={Share3Icon} alt="share" /></a>
          </div>
        </div>
      )}

      {selectedEvents.length > 0 ? (
        <>
          <div
            className={styles.calendar__event__title}
            dangerouslySetInnerHTML={{ __html: selectedDate }}
          />
          <EventList
            selectedEvents={selectedEvents
              .filter(event => event.hide_from_panel !== 'true' || !event.hide_from_panel)
              .sort((eventA, eventB) => {
                if (moment(eventA.start).utcOffset(eventA.timezone) > moment(eventB.start).utcOffset(eventB.timezone)) return 1;
                else if (moment(eventA.start).utcOffset(eventA.timezone) < moment(eventB.start).utcOffset(eventB.timezone)) return -1;
                else return 0;
              })}
            selectedDate={selectedDate}
          />
        </>
      ) : (
        <>
          <div
            className={styles.calendar__title}
            dangerouslySetInnerHTML={{ __html: 'The Pirate Calendar' }}
          />

          <Accordion classNames={{ content: styles.sidebar__event_types }} title='Calendars' type='calendar' isOpened={true} toggleDisabled>
            {eventTypes.map(renderCheckboxItem)}
          </Accordion>

          <Accordion title='About'>
            <div style={{ textAlign: 'center', fontSize: '17px', fontFamily: 'ProzaLibre-Medium', marginBottom: '14px' }}>Welcome to The Pirate Calendar!</div>
            <p>’Tis a place where you can find the ever increasing ways pirates explore, celebrate, and join together in this wondrous world.</p>
            <p>We’ve cobbled together an initial handful of categories we think might interest our fellow pirates.</p>
            <p style={{ textAlign: 'center' }}>However, we <span style={{ fontFamily: 'ProzaLibre-SemiBoldItalic' }}>hope</span> and <span style={{ fontFamily: 'ProzaLibre-SemiBoldItalic' }}>expect</span> that this list will soon <span style={{ fontFamily: 'ProzaLibre-SemiBoldItalic' }}><span style={{ fontSize: '15px' }}>g</span><span style={{ fontSize: '17px' }}>r</span><span style={{ fontSize: '19px' }}>o</span><span style={{ fontSize: '21px' }}>w</span><span style={{ fontSize: '22px' }}>!</span></span></p>
            <p>This calendar is a collaboration, brought to you by <span style={{ fontFamily: 'ProzaLibre-Italic' }}>your friendly neighborhood pirates</span>:</p>
            <div style={{ textAlign: 'center', fontSize: '16px', fontFamily: 'ProzaLibre-Medium' }}>Rare Thief:</div>
            <div style={{ marginBottom: '14px' }}>devoted pirate shepherds, helping their fellow pirates sail the seas with notably thorough written guides, video guides, and—of course—your trusty interactive SoT companion map.</div>
            <div style={{ textAlign: 'center', fontSize: '16px', fontFamily: 'ProzaLibre-Medium' }}>Taaamas:</div>
            <div style={{ marginBottom: '14px' }}>a sworn quartermaster, greeting discord with calm and clarity—and the creator of the beloved and beautiful monthly Sea of Thieves calendar art.</div>
            <p>For years, we've wanted to create such a calendar, but none of us could tackle the beast alone. But, together? Aye!</p>
            <p style={{ textAlign: 'center', fontFamily: 'ProzaLibre-Italic' }}>"We shall sail together!"</p>
          </Accordion>

          <Accordion title='Contact' noBreak>
            <p>If you’re planning an event that you think your fellow pirates would love to see on this calendar, let us know!</p>
            <p>Of course, with such a vibrant community, we can’t include every moment of planned piracy—lest this handy reference becomes a pictorial cacophony.</p>
            <p>That said, we hope to support as many pirate shenanigans as possible.</p>
            <p>contact@rarethief.com</p>
          </Accordion>
        </>
      )}

    </div>
  );
};

export default Sidebar;
